import React from 'react';
import styled from 'styled-components';

import { getTranslatedString } from 'helpers/i18n';
import { formatPhone } from 'helpers/Formatter';
import { ButtonPrimary } from '@yuhu-inc/yuhui';

const StyledMobileDuplicateRecordContainer = styled.div`
  display: flex;
  width: 100%;
  height: fit-content;
  flex-direction: column;
  padding: 0px;
  margin-bottom: 40px;
`;

const StyledDetailsContainer = styled.div`
  flex: 1;
  margin: 0px 10px;
`;

const StyledDetailsCardHeader = styled.div`
  padding: 0px 15px;
  margin-bottom: 10px;

  height: 20px;
  margin: 5px 0px;
`;

const StyledOriginalDetailsCard = styled.div`
  height: 90%;
  border: 1px solid #f7f7f7;
  border-radius: 8px;
  padding: 7px 15px;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
`;

const StyledScrollableContainer = styled.div`
  height: 90%;
  height: 100%;
  overflow-y: hidden;
  padding-right: 0px;
`;

const StyledDuplicateDetailsCard = styled.div`
  height: 370px;
  background-color: #fafafc;
  border: 1px solid #fafafc;
  border-radius: 8px;
  padding: 7px 15px;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  margin-bottom: ${(props) => (props.useBottomMargin ? '10px' : '0px')};
`;

const StyledDataRowContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${(props) => (props.padding ? props.padding : '0px 0px 10px')};
  border-bottom: ${(props) =>
    props.bottomBorder ? `1px solid ${props.bottomBorderColor}` : 'none'};
  font-size: ${(props) => (props.fontSize ? props.fontSize : '14px')};
`;

const StyledCtaContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: 10px;
  margin-top: 10px;
  flex-direction: column-reverse;
`;

const StyledDataRowSection = styled.div`
  display: flex;
  flex-direction: column;
  margin: ${(props) => (props.margin ? props.margin : '5px 0px')};
  text-align: ${(props) => (props.textAlign ? props.textAlign : 'left')};
`;

const StyledRowSectionHeader = styled.div`
  font-size: 12px;
  color: #828282;
  margin-top: 5px;
`;

const StyledViewModifyButton = styled(ButtonPrimary)`
  padding: 0px !important;
  background-color: transparent !important;
`;

const StyledH4 = styled.h4`
  margin-bottom: 0px;
`;

const MobileDuplicateTenantCard = ({ record, viewModify, mergeDuplicate }) => {
  let duplicateCount = record.duplicates.length;

  const handleViewModifyClick = (duplicate) => {
    viewModify(duplicate, record.original);
  };

  const handleMergeDuplicateClick = (duplicate) => {
    mergeDuplicate(duplicate, record.original);
  };

  return (
    <StyledMobileDuplicateRecordContainer>
      <OriginalTenant
        handleViewModifyClick={() => viewModify(record.original)}
        originalRecord={record.original}
      />
      <DuplicateRecords
        handleViewModifyClick={handleViewModifyClick}
        handleMergeDuplicateClick={handleMergeDuplicateClick}
        duplicateRecords={record.duplicates}
        duplicateCount={duplicateCount}
      />
    </StyledMobileDuplicateRecordContainer>
  );
};

export default MobileDuplicateTenantCard;

const OriginalTenant = ({ handleViewModifyClick, originalRecord }) => (
  <StyledDetailsContainer>
    <StyledDetailsCardHeader>
      {getTranslatedString('landlordPortal.dashboard.original')}
    </StyledDetailsCardHeader>
    <StyledOriginalDetailsCard>
      <StyledDataRowContainer bottomBorder={true} bottomBorderColor="#E8E8E8" fontSize="16px">
        <StyledH4>{originalRecord.full_name}</StyledH4>
        <StyledH4>
          {getTranslatedString('landlordPortal.dashboard.unit')} {originalRecord.lease.unit_number}
        </StyledH4>
      </StyledDataRowContainer>
      <StyledDataRowContainer bottomBorder={true} bottomBorderColor="#E8E8E8">
        <StyledDataRowSection>
          <StyledRowSectionHeader>
            {getTranslatedString('landlordPortal.dashboard.property')}
          </StyledRowSectionHeader>
          <div>{originalRecord.lease.property_name}</div>
        </StyledDataRowSection>
      </StyledDataRowContainer>
      <StyledDataRowContainer bottomBorder={true} bottomBorderColor="#E8E8E8">
        <StyledDataRowSection>
          <StyledRowSectionHeader>
            {getTranslatedString('landlordPortal.dashboard.leaseId')}
          </StyledRowSectionHeader>
          <div>{originalRecord.lease.client_external_id}</div>
        </StyledDataRowSection>
      </StyledDataRowContainer>
      <StyledDataRowContainer bottomBorder={true} bottomBorderColor="#E8E8E8" padding="5px">
        <StyledDataRowSection>
          <StyledRowSectionHeader>
            {getTranslatedString('landlordPortal.dashboard.email')}
          </StyledRowSectionHeader>
          <div>{originalRecord.email}</div>
        </StyledDataRowSection>
        <StyledDataRowSection textAlign="right" margin="17px 0px 5px">
          <StyledViewModifyButton
            onClick={handleViewModifyClick}
            contentKey="landlordPortal.dashboard.modifyEmail"
          />
        </StyledDataRowSection>
      </StyledDataRowContainer>
      <StyledDataRowContainer>
        <StyledDataRowSection>
          <StyledRowSectionHeader>
            {getTranslatedString('landlordPortal.dashboard.phoneNumber')}
          </StyledRowSectionHeader>
          <div>{formatPhone(originalRecord.phone_number)}</div>
        </StyledDataRowSection>
      </StyledDataRowContainer>
    </StyledOriginalDetailsCard>
  </StyledDetailsContainer>
);

const DuplicateRecords = ({
  handleViewModifyClick,
  handleMergeDuplicateClick,
  duplicateRecords,
  duplicateCount,
}) => {
  const onlyOneDuplicate = duplicateCount === 1;

  return (
    <StyledDetailsContainer>
      <StyledDetailsCardHeader>
        {getTranslatedString('landlordPortal.dashboard.duplicate')}({duplicateCount})
      </StyledDetailsCardHeader>
      <StyledScrollableContainer hideScroll={onlyOneDuplicate}>
        {duplicateRecords.map((duplicate) => (
          <StyledDuplicateDetailsCard useBottomMargin={!onlyOneDuplicate}>
            <StyledDataRowContainer bottomBorder={true} bottomBorderColor="#E8E8E8" fontSize="16px">
              <StyledH4>{duplicate.full_name}</StyledH4>
              <StyledH4>
                {getTranslatedString('landlordPortal.dashboard.unit')} {duplicate.lease.unit_number}
              </StyledH4>
            </StyledDataRowContainer>
            <StyledDataRowContainer bottomBorder={true} bottomBorderColor="#E8E8E8">
              <StyledDataRowSection>
                <StyledRowSectionHeader>
                  {getTranslatedString('landlordPortal.dashboard.property')}
                </StyledRowSectionHeader>
                <div>{duplicate.lease.property_name}</div>
              </StyledDataRowSection>
            </StyledDataRowContainer>
            <StyledDataRowContainer bottomBorder={true} bottomBorderColor="#E8E8E8">
              <StyledDataRowSection>
                <StyledRowSectionHeader>
                  {getTranslatedString('landlordPortal.dashboard.leaseId')}
                </StyledRowSectionHeader>
                <div>{duplicate.lease.client_external_id}</div>
              </StyledDataRowSection>
            </StyledDataRowContainer>
            <StyledDataRowContainer bottomBorder={true} bottomBorderColor="#E8E8E8" padding="5px">
              <StyledDataRowSection>
                <StyledRowSectionHeader>
                  {getTranslatedString('landlordPortal.dashboard.email')}
                </StyledRowSectionHeader>
                <div>{duplicate.unconfirmed_email}</div>
              </StyledDataRowSection>
              <StyledDataRowSection textAlign="right" margin="17px 0px 5px">
                <StyledViewModifyButton
                  onClick={() => handleViewModifyClick(duplicate)}
                  contentKey="landlordPortal.dashboard.modifyEmail"
                />
              </StyledDataRowSection>
            </StyledDataRowContainer>
            <StyledDataRowContainer>
              <StyledDataRowSection>
                <StyledRowSectionHeader>
                  {getTranslatedString('landlordPortal.dashboard.phoneNumber')}
                </StyledRowSectionHeader>
                <div>{formatPhone(duplicate.phone_number)}</div>
              </StyledDataRowSection>
            </StyledDataRowContainer>
            <StyledCtaContainer>
              <ButtonPrimary
                type="button"
                contentKey="landlordPortal.dashboard.mergeDuplicateTitle"
                onClick={() => handleMergeDuplicateClick(duplicate)}
              />
            </StyledCtaContainer>
          </StyledDuplicateDetailsCard>
        ))}
      </StyledScrollableContainer>
    </StyledDetailsContainer>
  );
};
